import {
  Box,
  Grid,
  Link,
  Slide,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { CreditCardImage } from 'components/images'
import { useCurrentStore, useOrdering, useCurrentUser } from 'hooks'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  validateCardCode,
  validateCardExpiry,
  validateCardNumber,
} from 'services'
import { SubmitOrderSectionProps } from './SubmitOrderSectionProps'
import { SubmitOrderSection } from './SubmitOrderSection'
import { KFieldLabel } from 'components/core/KFieldLabel'
import { OrderPaymentOptions } from './OrderPaymentOptions'
import { ApplePaySteps } from './ApplePaySteps'
import { KAddressField } from 'components/core/KAddressField'
import { KZipCodeField } from 'components/core/KZipCodeField'
import { KCreditCardNumberField } from 'components/core/KCreditCardNumberField'
import { KTextField } from 'components/core/KTextField'
import { KCreditCardValidThruField } from 'components/core/KCreditCardValidThruField'
import { KCreditCardCodeField } from 'components/core/KCreditCardCodeField'
import { AutofillStep, PaymentInfo, Place, StoredPaymentMethod } from 'models'
import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { AddressSelectionDialog } from 'components/account/AddressSelectionDialog'
import { CreditCardSelectionDialog } from 'components/account/CreditCardSelectionDialog'
import { Address, AddressType } from 'models/Address'

const SavedCardsLink = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation()
  const theme = useTheme()

  return (
    <Link
      underline="hover"
      sx={{
        color: theme.palette.primary.main,
        cursor: 'pointer',
        display: 'block',
        mt: 2,
      }}
      fontSize={theme.typography.body2.fontSize}
      onClick={onClick}
    >
      {t('Select from saved cards')}
    </Link>
  )
}

export const OrderPaymentInfo = ({ form }: SubmitOrderSectionProps) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [openAddressSelection, setOpenAddressSelection] = useState(false)
  const [openCardSelection, setOpenCardSelection] = useState(false)
  const { currentUser } = useCurrentUser()
  const { store } = useCurrentStore()
  const { order, saveOrderProps, autofillStep, setAutofillStep } = useOrdering()
  const hasAutoSelectedDefaultAddress = useRef(false)
  const hasAutoSelectedDefaultCard = useRef(false)
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [useNewCard, setUseNewCard] = useState(false)

  const [selectedCard, setSelectedCard] = useState<
    StoredPaymentMethod | undefined
  >(() => {
    if (order?.card?.payment_method_id && currentUser?.paymentMethods) {
      return currentUser.paymentMethods.find(
        (card) => card.id === order.card?.payment_method_id,
      )
    }
    return undefined
  })

  const billingAddresses = useMemo(
    () => currentUser?.billingAddresses || [],
    [currentUser?.billingAddresses],
  )
  const hasSavedBillingAddresses = billingAddresses.length > 0
  const hasSavedCards = (currentUser?.paymentMethods?.length || 0) > 0

  const defaultAddress = useMemo(
    () => billingAddresses.find((address) => address.default),
    [billingAddresses],
  )

  const currentlySelectedAddress = useMemo(
    () =>
      billingAddresses.find(
        (address) => address.id === order?.billing_place?.saved_address_id,
      ),
    [billingAddresses, order?.billing_place?.saved_address_id],
  )

  const defaultCard = useMemo(
    () => currentUser?.paymentMethods?.find((card) => card.default),
    [currentUser?.paymentMethods],
  )

  const {
    control,
    register,
    setValue,
    clearErrors,
    formState: { errors },
  } = form

  const isCreditCard = order?.payment_method === 'credit_card'
  const isApplePay = order?.payment_method === 'apple_pay'

  useEffect(() => {
    if (order?.card?.payment_method_id && currentUser?.paymentMethods) {
      const card = currentUser.paymentMethods.find(
        (card) => card.id === order.card?.payment_method_id,
      )
      setSelectedCard(card)

      if (card) {
        setUseNewCard(false)
      }
    }
  }, [order?.card?.payment_method_id, currentUser?.paymentMethods])

  const handleCloseCardDialog = () => {
    setOpenCardSelection(false)
  }

  const handleSelectCard = useCallback(
    (card: StoredPaymentMethod) => {
      setSelectedCard(card)
      setUseNewCard(false)

      const cardInfo = {
        payment_method_id: card.id,
      }

      saveOrderProps({ card: cardInfo })
      handleCloseCardDialog()
    },
    [saveOrderProps],
  )

  const handleUseNewCard = () => {
    setUseNewCard(true)
    setSelectedCard(undefined)
    saveOrderProps({
      card: {
        payment_method_id: undefined,
        number: '',
        name: '',
        expiry: '',
        cvc: '',
      },
    })

    setValue('paymentInfo.cardNumber', '')
    setValue('paymentInfo.cardName', '')
    setValue('paymentInfo.cardExpiry', '')
    setValue('paymentInfo.cardCode', '')

    clearErrors('paymentInfo.cardNumber')
    clearErrors('paymentInfo.cardName')
    clearErrors('paymentInfo.cardExpiry')
    clearErrors('paymentInfo.cardCode')
  }

  const setBillingAddressFields = useCallback(
    (value: Place | undefined) => {
      setValue('paymentInfo.billingPlace', value)
      setValue('paymentInfo.billingAddress', value?.description)
      setValue('paymentInfo.billingCity', value?.city)
      setValue('paymentInfo.billingState', value?.state)
      setValue('paymentInfo.billingZip', value?.zip_code)

      clearErrors('paymentInfo.billingPlace')
      clearErrors('paymentInfo.billingAddress')
      clearErrors('paymentInfo.billingCity')
      clearErrors('paymentInfo.billingState')
      clearErrors('paymentInfo.billingZip')

      saveOrderProps({
        billing_place: value,
        billing_address: value?.description,
        billing_city: value?.city,
        billing_state: value?.state,
        billing_zip: value?.zip_code,
      })
    },
    [setValue, clearErrors, saveOrderProps],
  )

  const useDeliveryAddress = () => {
    if (!order || !order.customer_place) return
    setBillingAddressFields(order.customer_place)
  }

  const openSelectAddressDialog = () => {
    setOpenAddressSelection(true)
  }

  const openSelectCardDialog = () => {
    setOpenCardSelection(true)
  }

  const handleSelectAddress = useCallback(
    (address: Address) => {
      if (!address) return

      const place: Place = {
        saved_address_id: address.id,
        description: address.address,
        street: address.street ?? address.address,
        city: address.city,
        state: address.state,
        zip_code: address.zip,
      }

      setBillingAddressFields(place)
    },
    [setBillingAddressFields],
  )

  const handleCloseAddressDialog = () => {
    setOpenAddressSelection(false)
  }

  useEffect(() => {
    if (
      !order?.billing_place?.place_id &&
      !hasAutoSelectedDefaultAddress.current &&
      autofillStep === AutofillStep.BillingAddress
    ) {
      defaultAddress && handleSelectAddress(defaultAddress)
      hasAutoSelectedDefaultAddress.current = true
      setAutofillStep(AutofillStep.DeliveryFee)
    }
  }, [
    defaultAddress,
    order?.billing_place?.place_id,
    handleSelectAddress,
    autofillStep,
    setAutofillStep,
  ])

  useEffect(() => {
    if (
      !order?.card?.payment_method_id &&
      !hasAutoSelectedDefaultCard.current &&
      autofillStep === AutofillStep.PaymentInfo
    ) {
      defaultCard && handleSelectCard(defaultCard)
      hasAutoSelectedDefaultCard.current = true
      setAutofillStep(AutofillStep.BillingAddress)
    }
  }, [
    defaultCard,
    order?.card?.payment_method_id,
    handleSelectCard,
    setAutofillStep,
    autofillStep,
  ])

  if (!store || !order) return null

  const renderCreditCardSection = () => (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      justifyContent="space-between"
    >
      <CreditCardInputs
        form={form}
        useNewCard={useNewCard}
        openSelectCardDialog={openSelectCardDialog}
      />
      {hasSavedCards && !useNewCard && (
        <Box sx={{ mr: { md: 8 } }}>
          <SavedCardsLink onClick={openSelectCardDialog} />
          <Link
            underline="hover"
            sx={{
              color: theme.palette.primary.main,
              cursor: 'pointer',
              display: 'block',
              mt: 1,
            }}
            fontSize={theme.typography.body2.fontSize}
            onClick={handleUseNewCard}
          >
            {t('Pay with a different card')}
          </Link>
        </Box>
      )}
    </Stack>
  )

  return (
    <SubmitOrderSection label={t('Payment info')}>
      {store.accept_apple_pay === true ? (
        <Stack spacing={2}>
          <Stack spacing={1}>
            <KFieldLabel>{t('Payment options')}</KFieldLabel>
            <OrderPaymentOptions form={form} />
          </Stack>
          {isCreditCard && (
            <Slide direction="left" in={isCreditCard}>
              {renderCreditCardSection()}
            </Slide>
          )}

          {isApplePay && (
            <Slide direction="left" in={isApplePay}>
              <Stack spacing={2}>
                <KFieldLabel>
                  {t('Follow below steps to pay for your order.')}
                </KFieldLabel>
                <ApplePaySteps />
              </Stack>
            </Slide>
          )}
        </Stack>
      ) : (
        renderCreditCardSection()
      )}

      <Typography variant="h4" sx={{ mt: 4 }}>
        {t('Billing info')}
      </Typography>
      <Grid container spacing={2.5} sx={{ mt: 0 }}>
        <Grid item xs={12} md={9.5}>
          <Controller
            name="paymentInfo.billingPlace"
            control={control}
            render={({ field }) => (
              <KAddressField
                label={t('Search your address')}
                helperText={errors?.paymentInfo?.billingPlace?.message}
                error={!!errors?.paymentInfo?.billingPlace}
                value={field.value}
                onChange={(value) => {
                  if (value?.place_id !== order?.billing_place?.place_id) {
                    setBillingAddressFields(value)
                  }
                }}
                ref={field.ref}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={2.5} display="flex" alignItems="center">
          <Stack
            direction="column"
            spacing={1}
            sx={{
              mt: { md: 4 },
            }}
          >
            {order.order_type === 'delivery' &&
              order.customer_place !== undefined && (
                <Link
                  underline="hover"
                  sx={{
                    color: theme.palette.primary.main,
                    marginTop: 4,
                    cursor: 'pointer',
                  }}
                  fontSize={theme.typography.body2.fontSize}
                  onClick={useDeliveryAddress}
                >
                  {t('Use delivery address')}
                </Link>
              )}

            {currentUser && hasSavedBillingAddresses && (
              <Link
                underline="hover"
                sx={{
                  color: theme.palette.primary.main,
                  marginTop: 4,
                  cursor: 'pointer',
                }}
                fontSize={theme.typography.body2.fontSize}
                onClick={openSelectAddressDialog}
              >
                {t('Select from previous addresses')}
              </Link>
            )}
          </Stack>
        </Grid>
      </Grid>
      <Grid container spacing={2.5} sx={{ mt: 0 }}>
        <Grid item xs={12} sm={6}>
          <KTextField
            required
            label={t('Billing address')}
            helperText={errors?.paymentInfo?.billingAddress?.message}
            error={!!errors?.paymentInfo?.billingAddress}
            {...register('paymentInfo.billingAddress', {
              required: t('Billing address is required'),
              onChange: (e) => {
                setValue('paymentInfo.billingPlace', undefined)
                clearErrors('paymentInfo.billingPlace')

                saveOrderProps({
                  billing_address: e.target.value,
                  billing_place: undefined,
                })
              },
            })}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <KTextField
            required
            label={t('City')}
            helperText={errors?.paymentInfo?.billingCity?.message}
            error={!!errors?.paymentInfo?.billingCity}
            {...register('paymentInfo.billingCity', {
              required: t('Billing city is required'),
              onChange: (e) => {
                setValue('paymentInfo.billingPlace', undefined)
                clearErrors('paymentInfo.billingPlace')

                saveOrderProps({
                  billing_city: e.target.value,
                  billing_place: undefined,
                })
              },
            })}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2.5} sx={{ mt: 0 }}>
        <Grid item xs={12} sm={6}>
          <KTextField
            required
            label={t('State')}
            helperText={errors?.paymentInfo?.billingState?.message}
            error={!!errors?.paymentInfo?.billingState}
            {...register('paymentInfo.billingState', {
              required: t('Billing state is required'),
              onChange: (e) => {
                setValue('paymentInfo.billingPlace', undefined)
                clearErrors('paymentInfo.billingPlace')

                saveOrderProps({
                  billing_state: e.target.value,
                  billing_place: undefined,
                })
              },
            })}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <KZipCodeField
            required
            label={t('Zip code')}
            helperText={errors?.paymentInfo?.billingZip?.message}
            error={!!errors?.paymentInfo?.billingZip}
            value={order?.billing_zip}
            {...register('paymentInfo.billingZip', {
              required: t('Zip code is required'),
              validate: (value) => {
                return value?.length === 5 || t('Invalid zip code')
              },
              onChange: (e) => {
                setValue('paymentInfo.billingPlace', undefined)
                clearErrors('paymentInfo.billingPlace')

                saveOrderProps({
                  billing_zip: e.target.value,
                  billing_place: undefined,
                })
              },
            })}
          />
        </Grid>
      </Grid>
      {currentUser && (
        <>
          <AddressSelectionDialog
            open={openAddressSelection}
            onClose={handleCloseAddressDialog}
            addresses={billingAddresses}
            addressType={AddressType.Billing}
            onSelectAddress={handleSelectAddress}
            selectedAddress={currentlySelectedAddress}
          />
          <CreditCardSelectionDialog
            open={openCardSelection}
            onClose={handleCloseCardDialog}
            selectedCard={selectedCard}
            onSelectCard={handleSelectCard}
          />
        </>
      )}
    </SubmitOrderSection>
  )
}

const CreditCardInputs = ({
  form,
  useNewCard = false,
  openSelectCardDialog,
}: SubmitOrderSectionProps & {
  useNewCard: boolean
  openSelectCardDialog: () => void
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { currentUser } = useCurrentUser()

  const {
    register,
    watch,
    getValues,
    formState: { errors },
  } = form

  const cardNumber = watch('paymentInfo.cardNumber')

  const { order, saveOrderProps } = useOrdering()
  const saveCardProps = (props: Partial<PaymentInfo>) =>
    saveOrderProps({ card: { ...order?.card, ...props } })

  if (!order) return null

  const isSavedCard = !!order.card?.payment_method_id && !useNewCard
  const selectedCard = currentUser?.paymentMethods?.find(
    (card) => card.id === order.card?.payment_method_id,
  )

  if (isSavedCard && selectedCard) {
    return (
      <Box sx={{ mt: 1 }}>
        <Typography variant="h2">
          {t('Paying with card ****')}
          {selectedCard.bogusCardNum}
        </Typography>
      </Box>
    )
  }

  return (
    <Grid container spacing={2.5}>
      <Grid item container xs={12} sm={6} spacing={2.5}>
        <Grid item xs={12}>
          <KCreditCardNumberField
            fullWidth
            required
            label={t('Card number')}
            helperText={errors?.paymentInfo?.cardNumber?.message}
            error={!!errors?.paymentInfo?.cardNumber}
            value={order.card?.number}
            {...register('paymentInfo.cardNumber', {
              required: t('Card number is required'),
              validate: (value) =>
                validateCardNumber(value) ? true : t('Invalid card number'),
              onChange: (e) => saveCardProps({ number: e.target.value }),
            })}
          />
        </Grid>
        <Grid item xs={12}>
          <KTextField
            fullWidth
            required
            label={t('Name on card')}
            helperText={errors?.paymentInfo?.cardName?.message}
            error={!!errors?.paymentInfo?.cardName}
            inputProps={{ maxLength: 100 }}
            value={order.card?.name}
            sx={{ '& input': { textTransform: 'uppercase' } }}
            {...register('paymentInfo.cardName', {
              required: t('Name is required'),
              onChange: (e) => saveCardProps({ name: e.target.value }),
            })}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <KCreditCardValidThruField
            fullWidth
            required
            label={t('Valid thru')}
            helperText={errors?.paymentInfo?.cardExpiry?.message}
            error={!!errors?.paymentInfo?.cardExpiry}
            value={order.card?.expiry}
            {...register('paymentInfo.cardExpiry', {
              required: t('Expiration date is required'),
              validate: (value) => validateCardExpiry(value),
              onChange: (e) => saveCardProps({ expiry: e.target.value }),
            })}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <KCreditCardCodeField
            fullWidth
            required
            label={t('CVC/CVV')}
            cardNumber={cardNumber}
            helperText={errors?.paymentInfo?.cardCode?.message}
            error={!!errors?.paymentInfo?.cardCode}
            value={getValues('paymentInfo.cardCode') ?? ''}
            {...register('paymentInfo.cardCode', {
              required: t('Verification code is required'),
              validate: (value) => {
                return validateCardCode(cardNumber, value)
                  ? true
                  : t('Invalid CVV/CVC code')
              },
              onChange: (e) => saveCardProps({ cvc: e.target.value }),
            })}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          sx={{
            [theme.breakpoints.up('sm')]: {
              display: 'none',
            },
          }}
        >
          {currentUser?.paymentMethods?.length ? (
            <SavedCardsLink onClick={openSelectCardDialog} />
          ) : null}
        </Grid>
      </Grid>
      <Grid
        item
        container
        xs={12}
        sm={6}
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          '& svg': { width: '50%', height: 'auto' },
          [theme.breakpoints.down('sm')]: { display: 'none' },
        }}
      >
        <CreditCardImage />
        {currentUser?.paymentMethods?.length ? (
          <SavedCardsLink onClick={openSelectCardDialog} />
        ) : null}
      </Grid>
    </Grid>
  )
}
